import { useState } from "react";

import { useRunOktaExport } from "src/api/hooks";
import { Button, Card } from "src/components/ui";
import type { Viewer } from "src/screens/types";

/**
 * Bulk export of current people to the Okta identity source.
 *
 * iam-admin only, and not because the button is dangerous to look at: the export
 * reads both populations. A writer of one would send an incomplete directory and
 * leave Okta missing everyone they cannot see. Hiding the tab is presentation.
 * `run_okta_export` is what refuses the call.
 */
export function OktaExport({ viewer }: { viewer: Viewer }) {
  const isAdmin = viewer.roles.includes("IAM_ADMIN");
  const run = useRunOktaExport();
  const [result, setResult] = useState<NonNullable<typeof run.data> | null>(null);

  if (!isAdmin) {
    return (
      <Card title="Export to Okta">
        <p className="text-sm text-slate-600">
          Only iam-admins can export to Okta. The export reads both populations, so anything
          narrower would leave people out.
        </p>
      </Card>
    );
  }

  if (!viewer.oktaExportEnabled) {
    return (
      <Card title="Export to Okta">
        <p className="text-sm text-slate-600">
          Okta export is not configured on this server. It needs the identity-source settings from
          the local Okta script: org URL, client id, key id, private key, and identity source id.
        </p>
      </Card>
    );
  }

  const report = result?.runOktaExport;

  return (
    <Card title="Export to Okta">
      <p className="text-sm text-slate-700">
        Gives system access to everyone with an active engagement who has never had it, then sends
        every person with system access who holds a current engagement to the Okta identity source.
        Anyone whose system access was removed is not given it back; restore it on their page. Their
        person id is the Okta external id and their work email is their username. Anyone without a
        work email is given first.last@need.ai, or first.last1@need.ai and up if that is taken, and
        it is saved on their record. Ending an engagement removes them from Okta; this export does
        not.
      </p>
      <div className="mt-4">
        <Button
          disabled={run.isPending}
          onClick={() => {
            setResult(null);
            run.mutate(undefined, { onSuccess: setResult });
          }}
        >
          {run.isPending ? "Exporting…" : "Export current people"}
        </Button>
      </div>
      {report ? (
        <div className="mt-4 text-sm text-slate-700">
          {report.granted > 0 ? (
            <p>
              Gave system access to <strong>{report.granted}</strong>{" "}
              {report.granted === 1 ? "person" : "people"} who had none.
            </p>
          ) : null}
          <p>
            Exported <strong>{report.exported}</strong>{" "}
            {report.exported === 1 ? "person" : "people"}
            {report.sessionId ? ` in session ${report.sessionId}` : ""}.
          </p>
          {report.skipped.length > 0 ? (
            <div className="mt-3">
              <p className="font-medium">Not sent</p>
              <ul className="mt-1 list-disc pl-5">
                {report.skipped.map((skip) => (
                  <li key={skip.identityId}>
                    {skip.name}: {skip.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
